import { useState } from "react";
import { Send, MessageSquare } from "lucide-react";
import { FIRM, PRACTICE_AREAS } from "@/lib/site";

export default function ConsultationForm({
  defaultPracticeArea = "",
  testId = "consultation-form",
}) {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    practiceArea: defaultPracticeArea,
    message: "",
  });
  const [status, setStatus] = useState("");

  const update = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent(
      `Free Consultation Request — ${form.name || "Website Inquiry"}`
    );
    const lines = [
      `Name: ${form.name}`,
      `Phone: ${form.phone}`,
      `Email: ${form.email}`,
      `Practice Area: ${form.practiceArea}`,
      ``,
      `Message:`,
      form.message,
    ];
    const body = encodeURIComponent(lines.join("\n"));
    window.location.href = `mailto:${FIRM.email}?subject=${subject}&body=${body}`;
    setStatus("Your default email app should now open with the inquiry pre-filled.");
  };

  return (
    <form
      onSubmit={handleSubmit}
      data-testid={testId}
      className="rounded-sm bg-white border border-navy-900/10 p-7 md:p-10 shadow-[0_30px_80px_-50px_rgba(10,25,47,0.4)]"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Field
          label="Full Name"
          required
          testId="contact-name"
          value={form.name}
          onChange={update("name")}
          placeholder="Jane Doe"
        />
        <Field
          label="Phone"
          type="tel"
          testId="contact-phone"
          value={form.phone}
          onChange={update("phone")}
          placeholder="(555) 555-5555"
        />
        <Field
          label="Email"
          type="email"
          required
          testId="contact-email"
          value={form.email}
          onChange={update("email")}
          placeholder="you@example.com"
        />
        <div className="flex flex-col gap-2">
          <label className="text-xs uppercase tracking-[0.18em] font-semibold text-navy-900/60">
            Practice Area
          </label>
          <select
            data-testid="contact-practice-area"
            value={form.practiceArea}
            onChange={update("practiceArea")}
            className="rounded-sm border border-navy-900/15 bg-white px-4 py-3.5 text-navy-900 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold transition"
          >
            <option value="">Select a practice area</option>
            {PRACTICE_AREAS.map((p) => (
              <option key={p.slug} value={p.title}>
                {p.title}
              </option>
            ))}
            <option value="Other">Other / Not sure</option>
          </select>
        </div>
      </div>
      <div className="mt-5 flex flex-col gap-2">
        <label className="text-xs uppercase tracking-[0.18em] font-semibold text-navy-900/60">
          How can we help?
        </label>
        <textarea
          data-testid="contact-message"
          value={form.message}
          onChange={update("message")}
          rows={5}
          required
          placeholder="Briefly describe your matter. Please don't include sensitive case details."
          className="rounded-sm border border-navy-900/15 bg-white px-4 py-3.5 text-navy-900 placeholder:text-navy-900/35 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold transition resize-none"
        />
      </div>
      <div className="mt-7 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
        <p className="text-xs text-navy-900/50 max-w-md leading-relaxed">
          By submitting, you agree this inquiry does not create an attorney–client
          relationship until a written agreement is signed.
        </p>
        <button
          type="submit"
          data-testid="contact-submit"
          className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-sm bg-gold text-navy-950 text-sm font-semibold hover:bg-gold-dark transition-all w-full sm:w-auto"
        >
          Request Free Consultation
          <Send className="h-4 w-4" />
        </button>
      </div>
      {status && (
        <div
          data-testid="contact-status"
          className="mt-5 rounded-sm bg-gold/10 border border-gold/40 px-4 py-3 text-sm text-navy-900/80 flex items-center gap-2"
        >
          <MessageSquare className="h-4 w-4 text-gold" />
          {status}
        </div>
      )}
    </form>
  );
}

function Field({ label, required, testId, ...props }) {
  return (
    <div className="flex flex-col gap-2">
      <label className="text-xs uppercase tracking-[0.18em] font-semibold text-navy-900/60">
        {label}
        {required && <span className="text-gold ml-1">*</span>}
      </label>
      <input
        {...props}
        required={required}
        data-testid={testId}
        className="rounded-sm border border-navy-900/15 bg-white px-4 py-3.5 text-navy-900 placeholder:text-navy-900/35 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold transition"
      />
    </div>
  );
}
